/**
 * LeaseTerms セクション (B2B リース・レンタル向け)。3 variants:
 *   month_term_table — 期間 (列) × 機種 (行) の月額マトリクス
 *   tiered_plans     — 期間別プランカード (24ヶ月 / 36ヶ月 / 60ヶ月)
 *   simple_table     — シンプルな条件一覧表
 */
import { Calendar } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { ArrayField, TextField, TextareaField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface MonthTermRow {
  modelName: string;
  cells: string[];
}
interface MonthTermConfig {
  title: string;
  subtitle: string;
  termHeaders: string[];
  rows: MonthTermRow[];
  notes: string;
}

interface TermPlan {
  termLabel: string;
  monthlyPrice: string;
  totalPrice: string;
  features: string[];
  recommended: boolean;
}
interface TieredPlansConfig {
  title: string;
  subtitle: string;
  plans: TermPlan[];
  notes: string;
}

interface SimpleTermRow {
  label: string;
  value: string;
}
interface SimpleTableConfig {
  title: string;
  rows: SimpleTermRow[];
  notes: string;
}

function MonthTermEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as MonthTermConfig;
  const headers = cfg.termHeaders ?? [];
  const rows = cfg.rows ?? [];
  const set = (patch: Partial<MonthTermConfig>) =>
    onChange({ ...config, ...patch });

  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <div className="space-y-1">
        <Label className="text-[11px]">期間ヘッダ + 月額</Label>
        <div className="overflow-x-auto">
          <table className="text-xs border border-[var(--border)] w-full">
            <thead>
              <tr>
                <th className="p-1 border-r border-[var(--border)]">機種</th>
                {headers.map((h, i) => (
                  <th key={i} className="p-1 border-r border-[var(--border)]">
                    <div className="flex gap-1">
                      <Input
                        value={h}
                        onChange={(e) => {
                          const next = [...headers];
                          next[i] = e.target.value;
                          set({ termHeaders: next });
                        }}
                        onBlur={onCommit}
                        placeholder="24 ヶ月"
                        className="text-xs w-20"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          set({
                            termHeaders: headers.filter((_, idx) => idx !== i),
                            rows: rows.map((r) => ({
                              ...r,
                              cells: r.cells.filter((_, idx) => idx !== i),
                            })),
                          });
                        }}
                        className="text-red-500"
                        aria-label="期間削除"
                      >
                        ×
                      </button>
                    </div>
                  </th>
                ))}
                <th className="p-1">
                  <button
                    type="button"
                    onClick={() => {
                      set({
                        termHeaders: [...headers, ''],
                        rows: rows.map((r) => ({ ...r, cells: [...r.cells, ''] })),
                      });
                    }}
                    className="text-[var(--accent)]"
                  >
                    + 期間
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-t border-[var(--border)]">
                  <td className="p-1">
                    <Input
                      value={r.modelName}
                      onChange={(e) => {
                        const next = rows.slice();
                        next[ri] = { ...r, modelName: e.target.value };
                        set({ rows: next });
                      }}
                      onBlur={onCommit}
                      placeholder="GH-2000"
                      className="text-xs w-24"
                    />
                  </td>
                  {r.cells.map((cell, ci) => (
                    <td key={ci} className="p-1 border-l border-[var(--border)]">
                      <Input
                        value={cell}
                        onChange={(e) => {
                          const cells = [...r.cells];
                          cells[ci] = e.target.value;
                          const next = rows.slice();
                          next[ri] = { ...r, cells };
                          set({ rows: next });
                        }}
                        onBlur={onCommit}
                        placeholder="¥9,800"
                        className="text-xs w-20 text-center"
                      />
                    </td>
                  ))}
                  <td className="p-1">
                    <button
                      type="button"
                      onClick={() =>
                        set({ rows: rows.filter((_, idx) => idx !== ri) })
                      }
                      className="text-red-500"
                      aria-label="行削除"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            set({
              rows: [
                ...rows,
                { modelName: '', cells: headers.map(() => '') },
              ],
            });
          }}
        >
          + 機種を追加
        </Button>
      </div>
      <TextareaField
        label="備考"
        value={cfg.notes ?? ''}
        onChange={(v) => set({ notes: v })}
        onCommit={onCommit}
        rows={2}
      />
    </div>
  );
}

function MonthTermRenderer({ config }: RendererProps) {
  const v = config as unknown as MonthTermConfig;
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-6">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-[var(--border)]">
          <thead className="bg-[var(--bg-elevated)]">
            <tr>
              <th className="p-3 text-left">機種</th>
              {(v.termHeaders ?? []).map((h, i) => (
                <th key={i} className="p-3 text-center font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(v.rows ?? []).map((r, i) => (
              <tr key={i} className="border-t border-[var(--border)]">
                <td className="p-3 font-medium">{r.modelName}</td>
                {r.cells.map((c, j) => (
                  <td key={j} className="p-3 text-center font-mono">
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {v.notes && (
        <p className="text-xs text-[var(--text-muted)] mt-3 whitespace-pre-line">
          {v.notes}
        </p>
      )}
    </section>
  );
}

function TieredPlansEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as TieredPlansConfig;
  const set = (patch: Partial<TieredPlansConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <ArrayField<TermPlan>
        label="期間別プラン"
        items={cfg.plans ?? []}
        onChange={(next) => set({ plans: next })}
        emptyItem={() => ({
          termLabel: '',
          monthlyPrice: '',
          totalPrice: '',
          features: [],
          recommended: false,
        })}
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <TextField
              label="期間ラベル"
              value={item.termLabel}
              onChange={(v) => update({ termLabel: v })}
              onCommit={onCommit}
              placeholder="36 ヶ月プラン"
            />
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="月額"
                value={item.monthlyPrice}
                onChange={(v) => update({ monthlyPrice: v })}
                onCommit={onCommit}
                placeholder="¥9,800/月"
              />
              <TextField
                label="期間総額"
                value={item.totalPrice}
                onChange={(v) => update({ totalPrice: v })}
                onCommit={onCommit}
                placeholder="¥352,800"
              />
            </div>
            <TextareaField
              label="含まれる項目 (1 行 = 1 項目)"
              value={(item.features ?? []).join('\n')}
              onChange={(v) =>
                update({ features: v.split('\n').filter((s) => s.trim()) })
              }
              onCommit={onCommit}
              rows={3}
            />
            <label className="flex items-center gap-2 text-[11px]">
              <input
                type="checkbox"
                checked={item.recommended}
                onChange={(e) => {
                  update({ recommended: e.target.checked });
                  onCommit();
                }}
              />
              このプランをおすすめ表示
            </label>
          </div>
        )}
      />
      <TextareaField
        label="備考"
        value={cfg.notes ?? ''}
        onChange={(v) => set({ notes: v })}
        onCommit={onCommit}
        rows={2}
      />
    </div>
  );
}

function TieredPlansRenderer({ config }: RendererProps) {
  const v = config as unknown as TieredPlansConfig;
  const plans = v.plans ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p, i) => (
          <div
            key={i}
            className={`relative rounded-xl border p-6 ${
              p.recommended
                ? 'border-[var(--accent)] border-2 bg-[var(--accent-subtle)]'
                : 'border-[var(--border)] bg-[var(--bg-elevated)]'
            }`}
          >
            {p.recommended && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold bg-[var(--accent)] text-white">
                おすすめ
              </span>
            )}
            <div className="text-base font-semibold mb-3">{p.termLabel}</div>
            <div className="mb-4">
              <div className="text-3xl font-bold">{p.monthlyPrice || '—'}</div>
              {p.totalPrice && (
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  期間総額 {p.totalPrice}
                </div>
              )}
            </div>
            <ul className="space-y-1.5 text-sm">
              {(p.features ?? []).map((f, j) => (
                <li key={j} className="text-[var(--text-secondary)]">
                  ・{f}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {v.notes && (
        <p className="text-xs text-[var(--text-muted)] mt-4 whitespace-pre-line">
          {v.notes}
        </p>
      )}
    </section>
  );
}

function SimpleTableEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as SimpleTableConfig;
  const rows = cfg.rows ?? [];
  const set = (patch: Partial<SimpleTableConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="タイトル"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <div className="space-y-1">
        <Label className="text-[11px]">条件項目</Label>
        {rows.map((row, i) => (
          <div key={i} className="flex gap-1">
            <Input
              value={row.label}
              onChange={(e) => {
                const next = rows.slice();
                next[i] = { ...row, label: e.target.value };
                set({ rows: next });
              }}
              onBlur={onCommit}
              placeholder="条件名"
              className="text-xs flex-1"
            />
            <Input
              value={row.value}
              onChange={(e) => {
                const next = rows.slice();
                next[i] = { ...row, value: e.target.value };
                set({ rows: next });
              }}
              onBlur={onCommit}
              placeholder="値"
              className="text-xs flex-[2]"
            />
            <button
              type="button"
              onClick={() => set({ rows: rows.filter((_, idx) => idx !== i) })}
              className="text-red-500 px-1"
            >
              ×
            </button>
          </div>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => set({ rows: [...rows, { label: '', value: '' }] })}
        >
          + 項目を追加
        </Button>
      </div>
      <TextareaField
        label="備考"
        value={cfg.notes ?? ''}
        onChange={(v) => set({ notes: v })}
        onCommit={onCommit}
        rows={2}
      />
    </div>
  );
}

function SimpleTableRenderer({ config }: RendererProps) {
  const v = config as unknown as SimpleTableConfig;
  return (
    <section>
      {v.title && <h3 className="text-xl font-semibold mb-3">{v.title}</h3>}
      <table className="w-full text-sm border border-[var(--border)]">
        <tbody>
          {(v.rows ?? []).map((r, i) => (
            <tr key={i} className="border-t border-[var(--border)]">
              <th className="p-3 text-left font-medium bg-[var(--bg-elevated)] w-1/3">
                {r.label}
              </th>
              <td className="p-3">{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {v.notes && (
        <p className="text-xs text-[var(--text-muted)] mt-3 whitespace-pre-line">
          {v.notes}
        </p>
      )}
    </section>
  );
}

export const leaseTermsDefinition: SectionDefinition = {
  type: 'lease_terms',
  label: 'リース条件',
  icon: Calendar,
  description: 'リース・レンタルの期間別料金や契約条件。',
  defaultVariant: 'tiered_plans',
  useCases: ['b2b_lease'],
  variants: {
    month_term_table: {
      label: '期間×機種マトリクス',
      description: '行=機種、列=期間で月額表示',
      Renderer: MonthTermRenderer,
      Editor: MonthTermEditor,
      sample: () =>
        ({
          title: 'リース料金表',
          subtitle: '機種・期間別の月額 (税抜)',
          termHeaders: ['24 ヶ月', '36 ヶ月', '60 ヶ月'],
          rows: [
            { modelName: 'GH-1000', cells: ['¥12,800', '¥9,800', '¥6,800'] },
            { modelName: 'GH-2000', cells: ['¥18,800', '¥14,800', '¥9,800'] },
            { modelName: 'GH-3000', cells: ['¥28,800', '¥22,800', '¥15,800'] },
          ],
          notes: '※ 価格は税抜表示。残価設定や保守オプションは別途。',
        }) as unknown as Record<string, unknown>,
    },
    tiered_plans: {
      label: '期間別カード',
      description: '24/36/60 ヶ月の階層カード',
      Renderer: TieredPlansRenderer,
      Editor: TieredPlansEditor,
      sample: () =>
        ({
          title: 'リースプラン',
          subtitle: '期間に応じてお得な月額を',
          plans: [
            {
              termLabel: '24 ヶ月プラン',
              monthlyPrice: '¥18,800/月',
              totalPrice: '¥451,200',
              features: ['標準サポート', '保守 12 ヶ月', '残価設定なし'],
              recommended: false,
            },
            {
              termLabel: '36 ヶ月プラン',
              monthlyPrice: '¥14,800/月',
              totalPrice: '¥532,800',
              features: [
                '優先サポート',
                '保守 36 ヶ月',
                '残価設定あり',
                '無償アップグレード',
              ],
              recommended: true,
            },
            {
              termLabel: '60 ヶ月プラン',
              monthlyPrice: '¥9,800/月',
              totalPrice: '¥588,000',
              features: ['プレミアムサポート', '保守 60 ヶ月', '入替保証'],
              recommended: false,
            },
          ] as TermPlan[],
          notes: '※ 価格は税抜。詳細はお問い合わせください。',
        }) as unknown as Record<string, unknown>,
    },
    simple_table: {
      label: 'シンプル条件表',
      description: '契約条件を key-value で',
      Renderer: SimpleTableRenderer,
      Editor: SimpleTableEditor,
      sample: () =>
        ({
          title: '契約条件',
          rows: [
            { label: '契約期間', value: '24 / 36 / 60 ヶ月' },
            { label: '月額料金', value: '¥6,800〜 (機種・期間による)' },
            { label: '初期費用', value: '不要' },
            { label: '保守', value: '契約期間内 無償' },
            { label: '残価設定', value: '36 ヶ月以上で利用可' },
          ] as SimpleTermRow[],
          notes: '※ 全価格税抜。詳細条件はお見積もりにて。',
        }) as unknown as Record<string, unknown>,
    },
  },
};
